import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  MenuItem,
} from "@mui/material";
import { getVehicles } from "../../services/vehicleService";
import { getParkers } from "../../services/parkerService";
import { updateBooking } from "../../services/bookingService";
import SuccessMessageModal from "../Reusable/SuccessMessageModal";
import WarningMessageModal from "../Reusable/WarningMessageModal";

// Helper para formatear la fecha para el input datetime-local
const formatDateTimeForInput = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  // Ajustar por la zona horaria para mostrar la hora local correcta en el input
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};

const EditBookingModal = ({ isOpen, onClose, booking }) => {
  const [vehicles, setVehicles] = useState([]);
  const [parkers, setParkers] = useState([]);
  const [bookingData, setBookingData] = useState({
    vehicle_id: "",
    parker_id: "",
    date: "",
    start_time: "",
    end_time: "",
    kw_acquired: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  useEffect(() => {
    if (booking) {
      const startDate = new Date(booking.start_date);
      const endDate = new Date(booking.end_date);
      setBookingData({
        vehicle_id: booking.vehicle_id || "",
        parker_id: booking.parker_id || "",
        date: startDate.toISOString().split('T')[0],
        start_time: `${startDate.getHours().toString().padStart(2, '0')}:00`,
        end_time: `${endDate.getHours().toString().padStart(2, '0')}:00`,
        kw_acquired: booking.kw_acquired || "",
      });
    }

    const fetchData = async () => {
      try {
        const vehiclesResponse = await getVehicles();
        setVehicles(vehiclesResponse.data);
        const parkersResponse = await getParkers();
        setParkers(parkersResponse.data);
      } catch (error) {
        setModalMessage("Error al cargar vehículos o parqueaderos.");
        setIsWarningOpen(true);
      }
    };
    fetchData();
  }, [booking]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBookingData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!bookingData.date || !bookingData.start_time || !bookingData.end_time) {
      setModalMessage("Todos los campos son obligatorios.");
      setIsWarningOpen(true);
      return;
    }

    const start_date = `${bookingData.date}T${bookingData.start_time}`;
    const end_date = `${bookingData.date}T${bookingData.end_time}`;

    const dataToSubmit = {
      ...bookingData,
      start_date,
      end_date,
      kw_acquired: parseFloat(bookingData.kw_acquired) || 0,
    };

    setIsLoading(true);
    try {
      await updateBooking(booking.booking_id, dataToSubmit);
      setModalMessage("Reserva actualizada exitosamente.");
      setIsSuccessOpen(true);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Error al actualizar la reserva.";
      setModalMessage(errorMessage);
      setIsWarningOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setIsSuccessOpen(false);
    onClose(true); // Indicar que se debe refrescar
  };

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>Editar Reserva</DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box display="flex" justifyContent="center"><CircularProgress /></Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField select label="Vehículo" name="vehicle_id" value={bookingData.vehicle_id} onChange={handleChange} fullWidth disabled={true}>
                {vehicles.map((vehicle) => (
                  <MenuItem key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                    {vehicle.plate} - {vehicle.model}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select label="Parqueadero" name="parker_id" value={bookingData.parker_id} onChange={handleChange} fullWidth disabled={true}>
                {parkers.map((parker) => (
                  <MenuItem key={parker.parker_id} value={parker.parker_id}>
                    {parker.location} - {parker.charger_type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="date"
                label="Fecha"
                type="date"
                value={bookingData.date}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                name="start_time"
                label="Hora de Inicio"
                value={bookingData.start_time}
                onChange={handleChange}
                fullWidth
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <MenuItem key={i} value={`${i.toString().padStart(2, '0')}:00`}>
                    {`${i.toString().padStart(2, '0')}:00`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                name="end_time"
                label="Hora de Fin"
                value={bookingData.end_time}
                onChange={handleChange}
                fullWidth
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <MenuItem key={i} value={`${i.toString().padStart(2, '0')}:00`}>
                    {`${i.toString().padStart(2, '0')}:00`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="kw_acquired"
                label="Kilowatts Adquiridos"
                type="number"
                value={bookingData.kw_acquired}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancelar</Button>
        <Button onClick={handleSave} variant="contained" disabled={isLoading}>Guardar Cambios</Button>
      </DialogActions>
      <SuccessMessageModal isOpen={isSuccessOpen} message={modalMessage} onClose={handleSuccessClose} />
      <WarningMessageModal isOpen={isWarningOpen} message={modalMessage} onClose={() => setIsWarningOpen(false)} />
    </Dialog>
  );
};

export default EditBookingModal;
